<template>
	<CardSelect 
		ref="card" 
		class="card-select-asset"
		:app="app"
		:card="card" 
		:actions="actions" 
		:guidance="guidance" 
		@init="handleInit" 
		@action="handleAction">

		<template #title>
			{{ title }}
		</template>

		<template #options>

			<OptionAsset 
				v-for="(asset, index) in assets" 
				refInFor 
				ref="option" 
				:id="asset.id"
				:index="index + 1" 
				:compact="isExpertMode"
				:asset="asset" 
				:fileKey="keyFileKeyDefault" 
				:animation="'dots'"
				:selected="selected === asset.id" 
				:selectable="true"
				:overmark="isOvermark"
				@click="handleOption(asset)" 
				@delete="handleDelete(asset)"
				@mouseenter="handleHover(asset)"
                @mouseleave="handleHover(null)" />

		</template>

		<template #post>

			<AtomToggle :active="slot">

				<template #first>
					<AtomText class="hide-on-mobile" :text="hint" />
				</template>

				<template #second v-if="isClonable">
					<div class="quick-actions">
						<span>No fitting {{ category }}?</span>
						<div class="buttons">
							<v-btn 
								elevation="0" 
								:class="{ 'button-active': isQuickActionTryAgain }"
								@click="handleTryAgain">
								{{ keyActionTryAgain.label }}
							</v-btn>
							<v-btn 
								elevation="0" 
								:class="{ 'button-active': isQuickActionEditDescription }"
								@click="handleEditDescription">
								{{ keyActionEditDescription.label }}
							</v-btn>
						</div>
					</div>
				</template>

			</AtomToggle>

        </template>

		<template v-if="isExpertMode" #side>

            <SidePanelExpert>
				<div class="expert-item">
                    <v-number-input
                        label="seed"
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        v-model="card.custom.seed">
                    </v-number-input>
                </div>
            </SidePanelExpert>
			
        </template>

	</CardSelect>
</template>

<script>
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import CATEGORY from '@generio-ai/library-legacy/constants/category'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import ACTION from '@/constants/action.js'
import CARD from '@/constants/card.js'
import AtomText from '@/components/common/atom/AtomText.vue'
import AtomToggle from '@/components/common/atom/AtomToggle.vue'
import CardSelect from '@/components/flow/cards/CardSelect.vue'
import OptionAsset from '@/components/flow/options/OptionAsset.vue'
import SidePanelExpert from '@/components/flow/sides/SidePanelExpert.vue'

export default {
	name: 'CardSelectAsset',
	components: {
		CardSelect,
		OptionAsset,
		AtomText,
		AtomToggle,
		SidePanelExpert
	},
	props: {
		app: {
            type: String,
            required: true
        },
		card: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			hovered: null,
		}
	},
	computed: {
		...mapGetters('flows', [
			'isExpertMode',
			'isActiveByIndex',
			'isLastByIndex',
			'isClonableByCard',
			'getPrimaryCardForCard'
		]),

		keyFileKeyDefault() {
			return ASSETS.FILES.KEY.DEFAULT
		},

		keyActionTryAgain() {
			return ACTION.TRY_AGAIN
		},
		keyActionEditDescription() {
			return ACTION.EDIT_DESCRIPTION
		},

		hasSelection() {
			return this.selected !== null
		},
		hasAssets() {
			return this.assets?.length > 0
		},

        isActiveCard() {
			return this.isActiveByIndex(this.app, this.index)
		},
		isLastCard() {
			return this.isLastByIndex(this.app, this.index)
		},

		isCategorySketch() {
			return this.category === CATEGORY.SKETCH
		},
		isCategoryPreview() {
			return this.category === CATEGORY.PREVIEW
		},
		isOvermark() {
			if (this.isCategorySketch) return true
			return false
		},
		isClonable() {
            return true // this.isClonableByCard(this.card)
        },
		isQuickActionTryAgain() {
			return ACTION.TRY_AGAIN.id === this.card.action
		},
		isQuickActionEditDescription() {
			return ACTION.EDIT_DESCRIPTION.id === this.card.action
		},

		allOptionsSelectable() {
			if (!this.hasAssets) return false
			const options = this.$refs.option || []
			const optionArray = Array.isArray(options) ? options : [options]
			return optionArray.every(option => option?.isReady)
		},

		index() {
			return this.card.index
		},
		nextIndex() {
			return this.index + 1
		},
		category() {
			return this.card.category
		},
		prompt() {
			return this.card.prompt
		},
		promptQuality() {
            return this.prompt.quality
        },
		promptSolid() {
			return this.prompt.solid
		},
		selected() {
			return this.card.option
		},
		assets() {
			return this.card.assets
		},

		primaryCard() {
			return this.getPrimaryCardForCard(this.app, this.card, false)
		},

		custom() {
			return this.card.custom
		},
		customSeed() {
			return this.custom?.seed
		},

		title() {
			if (this.category) return `Select your favorite ${this.category}.`
			return ''
		},
		slot() {
			if (this.hint) return 'first'
			if (this.hasSelection) return 'first'
			if (this.allOptionsSelectable) return 'second'
			return 'first'
		},
		hint() {
			if (this.hovered) return 'Left-click to select, right-click for options.'
			return null
		},
		guidance() {
			if (this.selected !== null) return null
			if (this.category) return `Please select your favorite ${this.category} to continue.`
			return 'Please wait a moment.'
		},
		actions() {
			if (this.isCategorySketch) {
				return [
					ACTION.GENERATE_PREVIEWS,
					ACTION.EDIT_SKETCH
				]
			}
			if (this.isCategoryPreview && this.prompt?.dual) {
				return [
					ACTION.GENERATE_TEXTURE,
				]
			}
			else if (this.isCategoryPreview) {
				return [
					ACTION.GENERATE_MODEL,
					ACTION.HIGHLIGHT_EDIT,
					ACTION.DESCRIBE_EDIT,
					ACTION.MAKE_SKETCH
				]
			}

			return []
		}
	},
	methods: {
		...mapActions('flows', [
			'removeCard',
			'newTransaction',
			'commitUpdateAssets',
			'fetchBase',
			'fetchMakeSketch',
			'fetchGenerateImages'
		]),
		handleInit(assets, isNew) {
			if (isNew) {
				this.card.custom ??= {}
				// only set defaults when undefined
				this.card.custom.seed ??= -1
			}
		},
		handleHover(asset) {
            this.hovered = asset
        },
		handleOption(asset) {
			if (this.selected === asset.id) return
			this.$refs.card?.reset()
			this.removeCard({
				app: this.app,
				index: this.nextIndex
			})
			this.$nextTick(() => {
				this.card.option = asset.id
			})
		},
		handleDelete(asset) {
			if (this.selected === asset.id) this.card.option = null
			this.card.assets = this.card.assets.filter(a => a.id !== asset.id)
		},
		handleTryAgain() {
			this.card.action = ACTION.TRY_AGAIN.id
			this.newTransaction({
				app: this.app,
				card: this.card,
				taskCommit: this.commitUpdateAssets({
					active: false
				}),
				taskFetch: this.card.transaction.taskFetch.clone()
			})
		},
		handleEditDescription() {
			this.card.action = ACTION.EDIT_DESCRIPTION.id
			this.newTransaction({
				app: this.app,
				card: this.card,
				taskCommit: this.commitUpdateAssets({
					active: false
				}),
				taskFetch: this.fetchBase({
					component: CARD.PROMPT,
					category: this.primaryCard.category,
				})
			})
		},
		handleAction(action) {
			switch(action.id) {
				case ACTION.DESCRIBE_EDIT.id:
					this.newTransaction({
						app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.PROMPT,
							category: this.category
						})
					})
					break  

				case ACTION.EDIT_SKETCH.id:
					this.newTransaction({
						app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.IMAGE,
							category: CATEGORY.SKETCH,
						})
					})
					break

				case ACTION.MAKE_SKETCH.id:
                    this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchMakeSketch({
							component: CARD.IMAGE
						})
					})
					break

				case ACTION.GENERATE_PREVIEWS.id:
					this.newTransaction({
						app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchGenerateImages({
							component: CARD.SELECT_ASSET,
							count: 3,
							seed: this.customSeed,
							preload: false
						})
					})
					break

				case ACTION.GENERATE_MODEL.id:
					this.newTransaction({
						app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.GENERATE_GEOMETRY,
							category: this.category
						})
					})
					break

				case ACTION.GENERATE_TEXTURE.id:
					this.newTransaction({
						app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.GENERATE_TEXTURE,
							category: this.category
						})
					})
					break

				case ACTION.HIGHLIGHT_EDIT.id:
					this.newTransaction({
						app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.IMAGE,
							category: CATEGORY.PHOTO,
						})
					})
					break
			}
		},
		handleKeyboardShortcuts(event) {

			// only if the card is active
			if (!this.isActiveCard) return

			// Get the pressed key
			const key = event.key

			if (key === 'Escape') {
				event.preventDefault()
				this.selected = null
			}

			// only if actions are not visible
			if (this.selected !== null) return

			// Check if the key is a single digit (1-9)
			if (/^[1-9]$/.test(key)) {

				// Convert to 0-indexed position
				const index = parseInt(key, 10) - 1

				// If an option exists at that index, trigger it
				if (index >= 0 && index < this.assets.length) {

					event.preventDefault()
					this.handleOption(this.assets[index])
				}
			}
		},
	},
	mounted() {
		window.addEventListener('keydown', this.handleKeyboardShortcuts)
	},
	beforeUnmount() {
        window.removeEventListener('keydown', this.handleKeyboardShortcuts)
    }
}
</script>

<style scoped>
.card-select-asset .quick-actions {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
}

.card-select-asset .quick-actions .buttons {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
}

.card-select-asset .quick-actions .button-active {
	color: rgb(var(--v-theme-white));
	background-color: rgb(var(--v-theme-color1));
}

@media (max-width: 600px) {
	.card-select-asset .quick-actions {
		flex-direction: column;
		gap: 5px;
	}

	.card-select-asset .quick-actions .buttons {
		gap: 5px;
	}
}
</style>